import { fromJS } from 'immutable';

import { authenticate, logOut } from '@/services/authentication';

import {
	DashboardAction,
	DashboardActionTypes
} from '@/modules/dashboard/dashboardTypes';
import type LoginError from './exceptions/loginError';
import type { ResetPasswordError } from './exceptions/resetPasswordError';
import { User, UserAction, UserActionTypes as actionTypes } from './userTypes';

const initialState = fromJS({
	authorize: {
		failure: {},
		isLoading: false,
		email: '',
		user: {
			features: {
				platformRedesign: false,
				aiHybridSearch: false
			}
		}
	},
	otp: {
		generateOtpSecret: {
			otpAuthUrl: '',
			loading: false,
			failure: false
		},
		enable: {
			recoveryCodes: null,
			loading: false,
			failure: false
		},
		deactivate: {
			loading: false,
			success: false,
			failure: false
		},
		verify: {
			failure: ''
		},
		reset: {
			failure: ''
		}
	},
	users: {
		data: [],
		failure: '',
		isLoading: false,
		editableUser: {}
	},
	isAuthorized: undefined,
	resetPassword: {
		isLoading: false,
		success: false,
		failure: {}
	},
	nylas: {
		authenticated: false
	},
	admin: {
		failure: ''
	},
	createUsers: {
		data: null,
		loading: false,
		error: null
	}
});

export default (state = initialState, action: UserAction | DashboardAction) => {
	switch (action.type) {
		case actionTypes.USER_VERIFY_SIGNUP_TOKEN:
			return state
				.setIn(['authorize', 'token'], action.payload)
				.setIn(['facebookFreemiumSuccess'], true);

		case actionTypes.USER_CHECK_USER_EXISTS:
			return state.setIn(['existingUser', 'isLoading'], true);

		case actionTypes.USER_CHECK_USER_EXISTS_SUCCESS:
			return state
				.setIn(['existingUser', 'isLoading'], false)
				.setIn(['existingUser', 'success'], action.payload);

		case actionTypes.USER_LOGIN_REQUEST:
			return state.setIn(['authorize', 'isLoading'], true);

		case actionTypes.USER_LOGIN_SUCCESS: {
			const { accessToken, refreshToken, ...userData } =
				action.payload as User;

			const {
				email,
				name,
				role,
				company: { name: companyName } = {},
				facebookFreemium
			} = userData;

			authenticate(
				name,
				role,
				email,
				companyName,
				accessToken,
				refreshToken,
				facebookFreemium
			);

			return state
				.set('isAuthorized', true)
				.setIn(['authorize', 'failure'], fromJS({}))
				.setIn(['authorize', 'isLoading'], false)
				.setIn(
					['authorize', 'user'],
					fromJS({
						...userData
					})
				);
		}
		case actionTypes.USER_LOGIN_FAILURE: {
			const { user, login } = action.error as LoginError;

			logOut();

			return state
				.setIn(['authorize', 'email'], user)
				.setIn(['authorize', 'isLoading'], false)
				.setIn(['authorize', 'failure'], fromJS(login));
		}

		case actionTypes.USER_LOGOUT_REQUEST_API: {
			return state.setIn(['authorize', 'user', 'logoutRequired'], true);
		}

		case actionTypes.USER_LOGOUT_SUCCESS: {
			setTimeout(() => {
				logOut();
				setTimeout(() => {
					window.location.reload();
				}, 500);
			}, 200);

			const authorizeFailure = state
				.getIn(['authorize', 'failure'])
				.toJS();
			return state
				.set('isAuthorized', false)
				.setIn(['authorize', 'user', 'logoutRequired'], false)
				.setIn(
					['authorize', 'failure'],
					fromJS(
						action.error
							? (action.error as LoginError).login
							: authorizeFailure
					)
				);
		}
		case actionTypes.USER_GET_USER_REQUEST:
			return state
				.setIn(['authorize', 'isLoading'], true)
				.setIn(['nylas', 'authenticated'], false);
		case actionTypes.USER_GET_USER_SUCCESS: {
			const {
				email,
				name,
				role,
				company: { name: companyName },
				accessToken
			} = action.payload as Required<User>;

			authenticate(name, role, email, companyName, accessToken);

			return state
				.setIn(['authorize', 'user'], fromJS(action.payload))
				.setIn(['authorize', 'isLoading'], false);
		}

		case actionTypes.USER_GET_USERS_REQUEST: {
			return state.setIn(['users', 'isLoading'], true);
		}
		case actionTypes.USER_GET_USERS_SUCCESS: {
			return state
				.setIn(['users', 'data'], fromJS(action.payload))
				.setIn(['users', 'isLoading'], false);
		}
		case actionTypes.USER_GET_USER_BY_EMAIL_SUCCESS: {
			return state
				.setIn(['editableUser', 'data'], action.payload)
				.setIn(['editableUser', 'err'], '');
		}
		case actionTypes.USER_GET_USER_BY_EMAIL_FAILURE: {
			return state
				.setIn(['editableUser', 'data'], {})
				.setIn(['editableUser', 'err'], action.payload);
		}
		case actionTypes.USER_GET_USER_BY_EMAIL_NO_USER: {
			return state
				.setIn(['editableUser', 'data'], {})
				.setIn(['editableUser', 'err'], 'no-user');
		}
		case actionTypes.USER_UPDATE_USER_ADMIN_SUCCESS: {
			return state
				.setIn(
					['editableUser', 'data', 'aiCredits'],
					action.payload.aiCredits
				)
				.setIn(
					['editableUser', 'data', 'emailLimit'],
					action.payload.emailLimit || 50
				)
				.setIn(['editableUser', 'err'], '');
		}
		case actionTypes.USER_UPDATE_USER_ADMIN_FAILURE: {
			return state.setIn(['editableUser', 'err'], action.payload);
		}
		case actionTypes.USER_RESET_PASSWORD_REQUEST:
			return state.setIn(['resetPassword', 'isLoading'], true);
		case actionTypes.USER_RESET_PASSWORD_SUCCESS:
			return state
				.setIn(['resetPassword', 'isLoading'], false)
				.setIn(['resetPassword', 'success'], true)
				.setIn(['resetPassword', 'failure'], fromJS({}));

		case actionTypes.USER_RESET_PASSWORD_FAILURE: {
			const { resetPassword } = action.error as ResetPasswordError;

			return state
				.setIn(['resetPassword', 'isLoading'], false)
				.setIn(['resetPassword', 'success'], false)
				.setIn(['resetPassword', 'failure'], fromJS(resetPassword));
		}

		case actionTypes.USER_ADMIN_RESET_PASSWORD_REQUEST:
			return state.setIn(['adminResetPassword', 'isLoading'], true);

		case actionTypes.USER_ADMIN_RESET_PASSWORD_SUCCESS:
			return state
				.setIn(['adminResetPassword', 'isLoading'], false)
				.setIn(['adminResetPassword', 'success'], true)
				.setIn(['adminResetPassword', 'failure'], fromJS({}));

		case actionTypes.USER_ADMIN_RESET_PASSWORD_FAILURE: {
			const { resetPassword } = action.error as ResetPasswordError;
			return state
				.setIn(['adminResetPassword', 'isLoading'], false)
				.setIn(['adminResetPassword', 'success'], false)
				.setIn(
					['adminResetPassword', 'failure'],
					fromJS(resetPassword)
				);
		}

		case actionTypes.USER_VALIDATE_EMAIL_REQUEST:
			return state.setIn(['authorize', 'user', 'emailRequested'], true);
		case actionTypes.USER_UPDATE_EMAILS_LEFT:
			const newLimit =
				Number(state.getIn(['authorize', 'user', 'emailLimitLeft'])) -
				1;
			return state.setIn(
				['authorize', 'user', 'emailLimitLeft'],
				newLimit
			);

		case actionTypes.USER_UPDATE_EMAILS_LEFT_LOCAL:
			return state.setIn(
				['authorize', 'user', 'emailLimitLeft'],
				action.payload
			);
		case actionTypes.USER_INCREASE_EMAILS_LEFT:
			return state.setIn(
				['authorize', 'user', 'emailLimitLeft'],
				Number(state.getIn(['authorize', 'user', 'emailLimitLeft'])) + 1
			);
		case actionTypes.USER_ADD_EMAIL_TOKEN_SUCCESS:
			return state
				.setIn(
					['authorize', 'user', 'threadedEmail'],
					action.payload?.user?.threadedEmail
				)
				.setIn(['authorize', 'user', 'emailValid'], true)
				.setIn(['authorize', 'user', 'emailTokenValid'], true)
				.setIn(['authorize', 'user', 'emailTokenInvalid'], false)
				.setIn(['authorize', 'user', 'emailTokenExpired'], false);
		case actionTypes.USER_ADD_EMAIL_TOKEN_FAILURE:
			if (action.payload === 'invalid') {
				return state
					.setIn(['authorize', 'user', 'emailTokenInvalid'], true)
					.setIn(['authorize', 'user', 'emailTokenExpired'], false)
					.setIn(['authorize', 'user', 'emailTokenValid'], false);
			}

			return state
				.setIn(['authorize', 'user', 'emailTokenInvalid'], false)
				.setIn(['authorize', 'user', 'emailTokenExpired'], true)
				.setIn(['authorize', 'user', 'emailTokenValid'], false);
		case actionTypes.USER_TOGGLE_ADD_USER_MODAL:
			return state
				.setIn(['authorize', 'addUserModalOpen'], action.payload)
				.setIn(['addUser', 'loading'], false)
				.setIn(['addUser', 'success'], false);

		case actionTypes.USER_ADD_NEW_USER_LOADING:
			return state.setIn(['addUser', 'loading'], true);

		case actionTypes.USER_ADD_NEW_USER_SUCCESS:
			return state
				.setIn(['addUser', 'loading'], false)
				.setIn(['addUser', 'success'], true);

		case actionTypes.USER_DELETE_USER_REQUEST:
			return state.setIn(['editableUser', 'delete', 'success'], false);

		case actionTypes.USER_DELETE_USER_SUCCESS:
			return state
				.setIn(['editableUser', 'data'], {})
				.setIn(['editableUser', 'delete', 'success'], true);

		case actionTypes.USER_TOGGLE_ADD_BCC_MODAL:
			return state
				.setIn(['authorize', 'addBccModalOpen'], action.payload)
				.setIn(['addBcc', 'loading'], false)
				.setIn(['addBcc', 'success'], false);

		case actionTypes.USER_ADD_NEW_BCC_LOADING:
			return state
				.setIn(['addBcc', 'success'], false)
				.setIn(['addBcc', 'loading'], true)
				.setIn(['addBcc', 'failure'], false);

		case actionTypes.USER_ADD_NEW_BCC_SUCCESS:
			const user = { ...state.getIn(['authorize', 'user']).toJS() };
			if (!user.emailLimitLeft) {
				user.emailLimitLeft = user.emailLimit;
			}
			return state
				.setIn(['addBcc', 'loading'], false)
				.setIn(['addBcc', 'success'], true)
				.setIn(['addBcc', 'failure'], false)
				.setIn(['authorize', 'user'], fromJS(action.payload))
				.setIn(
					['authorize', 'user', 'emailLimitLeft'],
					fromJS(user.emailLimitLeft)
				);

		case actionTypes.USER_ADD_NEW_BCC_FAILURE:
			return state
				.setIn(['addBcc', 'loading'], false)
				.setIn(['addBcc', 'success'], false)
				.setIn(['addBcc', 'failure'], true);

		case actionTypes.USER_ADD_NEW_BCC_SUCCESS_OFF:
			return state
				.setIn(['addBcc', 'success'], false)
				.setIn(['addBcc', 'failure'], false)
				.setIn(['addBcc', 'loading'], false);

		case actionTypes.USER_CREATE_SCHEDULE_REQUEST:
			return state
				.setIn(['authorize', 'user', 'scheduleLinkRequest'], true)
				.setIn(['authorize', 'user', 'scheduleLinkCreated'], false)
				.setIn(['authorize', 'user', 'calendarAuth'], false);

		case actionTypes.USER_CREATE_SCHEDULE_SUCCESS:
			return state
				.setIn(
					['authorize', 'user', 'scheduleLink'],
					action.payload?.scheduleLink
				)
				.setIn(['authorize', 'user', 'scheduleLinkCreated'], true)
				.setIn(['authorize', 'user', 'scheduleLinkRequest'], false)
				.setIn(['authorize', 'user', 'calendarAuth'], false);

		case actionTypes.USER_CREATE_SCHEDULE_CAL_AUTH:
			return state.setIn(['authorize', 'user', 'calendarAuth'], true);
		case actionTypes.USER_ADMIN_ACCESS_ACCOUNT_FAILURE: {
			if (action.payload === 'Can not access admin account') {
				return state.setIn(
					['admin', 'failure'],
					'You do not have permission to access another admin account.'
				);
			}
			if (action.payload === 'Permission denied') {
				return state.setIn(
					['admin', 'failure'],
					'Your account does not have permission to access other accounts.'
				);
			}
		}
		case actionTypes.USER_ADMIN_ACCESS_ACCOUNT_CLEAR_STATUS: {
			return state.setIn(['admin', 'failure'], '');
		}

		case actionTypes.USER_REFRESH_TOKEN_REQUEST:
			return state.setIn(['authorize', 'user', 'refreshRequired'], true);

		case actionTypes.USER_REFRESH_TOKEN_SUCCESS:
			const { token, reload, resetTimeout } = action.payload;
			localStorage.setItem('user_token', token);
			if (reload) {
				setTimeout(() => {
					window.location.reload();
				}, 200);
			}
			if (resetTimeout) {
				return state
					.setIn(['authorize', 'user', 'refreshRequired'], false)
					.setIn(
						['authorize', 'user', 'resetTokenTimeout'],
						Math.random()
					);
			}
			return state.setIn(['authorize', 'user', 'refreshRequired'], false);

		case actionTypes.USER_GET_NEW_COMMUNICATIONS_SUCCESS:
			if (action.payload.newMessages > 0) {
				return state.setIn(['authorize', 'newMessages'], true);
			}
			return state.setIn(['authorize', 'newMessages'], false);

		case actionTypes.USER_GET_NOTIFICATIONS_SUCCESS:
			const { payload } = action;
			return state.setIn(['authorize', 'user', 'notifications'], payload);

		case actionTypes.USER_SIGN_UP_FACEBOOK_FREEMIUM:
			if (action.payload) {
				return state.setIn(
					['authorize', 'user', 'facebookFreemium'],
					true
				);
			}
			return state.setIn(
				['authorize', 'user', 'facebookFreemium'],
				false
			);

		case actionTypes.USER_GENERATE_OTP_SECRET: {
			return state
				.setIn(['otp', 'generateOtpSecret', 'loading'], true)
				.setIn(['otp', 'generateOtpSecret', 'failure'], false)
				.setIn(['otp', 'generateOtpSecret', 'authUrl'], '');
		}
		case actionTypes.USER_GENERATE_OTP_SECRET_SUCCESS: {
			const { otpAuthUrl } = action.payload;

			return state
				.setIn(['otp', 'generateOtpSecret', 'authUrl'], otpAuthUrl)
				.setIn(['otp', 'generateOtpSecret', 'loading'], false);
		}
		case actionTypes.USER_GENERATE_OTP_SECRET_FAILURE: {
			return state
				.setIn(['otp', 'generateOtpSecret', 'failure'], true)
				.setIn(['otp', 'generateOtpSecret', 'loading'], false);
		}
		case actionTypes.USER_ENABLE_OTP: {
			return state
				.setIn(['otp', 'enable', 'recoveryCodes'], null)
				.setIn(['otp', 'enable', 'loading'], true)
				.setIn(['otp', 'enable', 'failure'], false);
		}
		case actionTypes.USER_ENABLE_OTP_SUCCESS: {
			const user = state.getIn(['authorize', 'user']).toJS();
			const updatedUser = { ...user, otpVerified: true };

			return state
				.setIn(['otp', 'enable', 'recoveryCodes'], action.payload)
				.setIn(['otp', 'enable', 'loading'], false)
				.setIn(['authorize', 'user'], fromJS(updatedUser));
		}
		case actionTypes.USER_ENABLE_OTP_FAILURE: {
			return state
				.setIn(['otp', 'enable', 'failure'], true)
				.setIn(['otp', 'enable', 'loading'], false);
		}
		case actionTypes.USER_DEACTIVATE_OTP: {
			return state
				.setIn(['otp', 'deactivate', 'loading'], true)
				.setIn(['otp', 'deactivate', 'success'], false)
				.setIn(['otp', 'deactivate', 'failure'], false);
		}
		case actionTypes.USER_DEACTIVATE_OTP_SUCCESS: {
			const user = state.getIn(['authorize', 'user']).toJS();
			const updatedUser = { ...user, otpVerified: false };

			return state
				.setIn(['otp', 'deactivate', 'loading'], false)
				.setIn(['otp', 'deactivate', 'success'], true)
				.setIn(['otp', 'deactivate', 'failure'], false)
				.setIn(['authorize', 'user'], fromJS(updatedUser));
		}
		case actionTypes.USER_DEACTIVATE_OTP_FAILURE: {
			return state
				.setIn(['otp', 'deactivate', 'loading'], false)
				.setIn(['otp', 'deactivate', 'success'], false)
				.setIn(['otp', 'deactivate', 'failure'], true);
		}
		case actionTypes.USER_CLEAR_OTP_SUCCESS_FAILURE_STATES: {
			return state
				.setIn(['otp', 'deactivate', 'success'], false)
				.setIn(['otp', 'deactivate', 'failure'], false)
				.setIn(['otp', 'enable', 'failure'], false)
				.setIn(['otp', 'generateOtpSecret', 'failure'], false);
		}
		case actionTypes.USER_CLEAR_OTP_STATE: {
			return state
				.setIn(['otp', 'enable', 'recoveryCodes'], null)
				.setIn(['otp', 'generateOtpSecret', 'authUrl'], '');
		}
		case actionTypes.USER_ADD_OTP_STATE_TOKEN: {
			const { otpStateToken, email } = action.payload;

			return state
				.setIn(['otp', 'stateToken'], otpStateToken)
				.setIn(['otp', 'email'], email);
		}
		case actionTypes.USER_VERIFY_OTP_FAILURE: {
			let failureMessage = action.payload;

			if (!failureMessage) {
				failureMessage = 'Something went wrong. Please try again.';
			}

			return state.setIn(['otp', 'verify', 'failure'], failureMessage);
		}
		case actionTypes.USER_RESET_OTP_FAILURE: {
			let failureMessage = action.payload;

			if (!failureMessage) {
				failureMessage = 'Something went wrong. Please try again.';
			}

			return state.setIn(['otp', 'reset', 'failure'], failureMessage);
		}

		case actionTypes.USER_ADMIN_CREATE_ACCOUNTS_REQUEST:
			return state
				.setIn(['createUsers', 'loading'], true)
				.setIn(['createUsers', 'error'], null);

		case actionTypes.USER_ADMIN_CREATE_ACCOUNTS_SUCCESS:
			return state
				.setIn(['createUsers', 'loading'], false)
				.setIn(['createUsers', 'data'], action.payload);

		case actionTypes.USER_ADMIN_CREATE_ACCOUNTS_FAILURE:
			return state
				.setIn(['createUsers', 'loading'], false)
				.setIn(
					['createUsers', 'error'],
					action.payload.message || action.payload.toString()
				);

		case actionTypes.USER_UPDATE_EMAILS_LIMIT_REQUEST:
		case actionTypes.USER_UPDATE_EMAILS_LIMIT_SUCCESS:
		case actionTypes.USER_UPDATE_EMAILS_LIMIT_FAILURE:
		case actionTypes.USER_VALIDATE_EMAIL_SUCCESS:
		case actionTypes.USER_VALIDATE_EMAIL_FAILURE:
		case actionTypes.USER_GET_USER_FAILURE:
		case actionTypes.USER_GET_USERS_FAILURE:
		case actionTypes.USER_LOGOUT_FAILURE:
		default:
			return state;
	}
};
